import type { Match, Mode, NewMatch } from './types';

/**
 * Hoelang een potje open staat. Binnen die week mag je de stand rechtzetten en
 * mag een beheerder hem terugtrekken; daarna ligt hij vast. Zie migratie 020 —
 * `vaststeltermijn()` daar hoort hetzelfde getal te zijn, en test-schema.mjs
 * bewaakt dat.
 */
export const VASTSTELTERMIJN_DAGEN = 7;

/**
 * Geteld vanaf het invoeren, niet vanaf het spelen: wie een potje van vorige
 * week vandaag invoert krijgt dezelfde week om het recht te zetten. Een
 * correctie zet `createdAt` opnieuw, dus dan begint de week van voren af aan.
 */
export function ligtVast(potje: Match): boolean {
  const open = VASTSTELTERMIJN_DAGEN * 86_400_000;
  return Date.now() - new Date(potje.createdAt).getTime() > open;
}

export interface PotjeFormulier {
  pouleId: string | null;
  mode: Mode;
  a1: string;
  a2: string;
  b1: string;
  b2: string;
  scoreA: string;
  scoreB: string;
  datum: string;
  tijd: string;
  tournamentId?: string | null;
  teltMee?: boolean;
}

/* Een letterlijke `ok` maakt het een echte discriminated union: TypeScript kan
   dan zien dat `potje` bestaat zodra `ok` waar is. Een controle op alleen
   `fout` zou dat niet doen, want een lege string is ook falsy. */
export type Validatie = { ok: false; fout: string } | { ok: true; potje: NewMatch };

/**
 * De vier validaties op een handmatig ingevoerde uitslag. Dezelfde controles
 * staan in record_match() in migratie 001, zodat de database ook weigert wat
 * hier langs zou glippen.
 */
export function valideerPotje(f: PotjeFormulier): Validatie {
  const tweeTegenTwee = f.mode === '2v2';
  const teamA = tweeTegenTwee ? [f.a1, f.a2] : [f.a1];
  const teamB = tweeTegenTwee ? [f.b1, f.b2] : [f.b1];
  const alle = [...teamA, ...teamB];

  /* 1 — iedereen ingevuld en niemand dubbel */
  if (alle.some((x) => !x)) return { ok: false, fout: 'Kies eerst alle spelers.' };
  if (new Set(alle).size !== alle.length) return { ok: false, fout: 'Dezelfde speler staat twee keer opgesteld.' };

  /* 2 — geldige standen */
  const scoreA = Number(f.scoreA);
  const scoreB = Number(f.scoreB);
  if (
    f.scoreA === '' ||
    f.scoreB === '' ||
    !Number.isInteger(scoreA) ||
    !Number.isInteger(scoreB) ||
    scoreA < 0 ||
    scoreB < 0 ||
    scoreA > 10 ||
    scoreB > 10
  ) {
    return { ok: false, fout: 'Vul voor beide teams een heel getal van 0 tot en met 10 in.' };
  }

  /* 3 — er moet een winnaar zijn */
  if (scoreA === scoreB) {
    return { ok: false, fout: 'Gelijkspel bestaat niet aan de tafel — er moet een winnaar zijn.' };
  }

  if (!f.pouleId) return { ok: false, fout: 'Kies eerst een poule.' };

  /* 4 — niet in de toekomst */
  const wanneer = new Date(`${f.datum}T${f.tijd || '00:00'}`);
  if (Number.isNaN(wanneer.getTime())) return { ok: false, fout: 'Datum of tijd is ongeldig.' };
  if (wanneer.getTime() > Date.now() + 60_000) return { ok: false, fout: 'Dat tijdstip ligt in de toekomst.' };

  return {
    ok: true,
    potje: {
      pouleId: f.pouleId,
      playedAt: wanneer.toISOString(),
      mode: f.mode,
      teamA,
      teamB,
      scoreA,
      scoreB,
      tournamentId: f.tournamentId ?? null,
      teltMee: f.teltMee !== false
    }
  };
}
